/**
 * Slice 14 — Clean step with timer.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { readSimpleFlowState, skippedMaintenanceTitles, type SimpleFlowState } from "@/lib/simpleTurnover";
import { cn } from "@/lib/utils";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { effectiveArrivalTasks, parseArrivalTasks, readArrivalAnswers, type ArrivalTask } from "@/lib/arrivalChecklist";
import { getTaskText, type Room } from "@/types/checklist";
import { ArrivalChecklistStep, ProgressBars, TickBox } from "@/components/cleaner/ArrivalChecklistStep";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { PhotoNeedNag } from "@/components/cleaner/PhotoNeedNag";

interface Props {
  propertyId: string;
  taskId: string;
  entryWalkthroughState: unknown;
  startedAt?: string | null;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  onComplete: () => void;
  onReportIssue?: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
  /** Undo a skipped maintenance check — rewinds to Maint segment. */
  onUndoMaintenanceSkip?: () => void;
  /** Checklists assigned to this job (cleaning checklist shown in Clean). */
  checklistIds?: string[];
}

type CleanItem = { key: string; room: string; text: string };

async function loadCleanStepData(taskId: string, propertyId: string, checklistIds: string[]) {
  let arrival: ArrivalTask[] = [];
  let items: CleanItem[] = [];
  try {
    const { data: row, error } = await supabase
      .from("cleaning_tasks")
      .select("arrival_tasks, checklist_id")
      .eq("id", taskId)
      .maybeSingle();
    if (error) throw error;
    const jobRow = row as unknown as { arrival_tasks: unknown; checklist_id: string | null } | null;
    const snap = parseArrivalTasks(jobRow?.arrival_tasks);
    if (snap !== null) {
      arrival = snap;
    } else {
      const { data: prop, error: propErr } = await supabase
        .from("properties")
        .select("arrival_checklist")
        .eq("id", propertyId)
        .maybeSingle();
      if (propErr) throw propErr;
      arrival = effectiveArrivalTasks((prop as unknown as { arrival_checklist: unknown } | null)?.arrival_checklist).tasks;
    }
    const ids = checklistIds.length ? checklistIds : jobRow?.checklist_id ? [jobRow.checklist_id] : [];
    if (ids.length) {
      const { data: lists } = await supabase.from("checklists").select("id, type, rooms").in("id", ids);
      const seen = new Set<string>();
      for (const list of (lists ?? []) as { id: string; type: string | null; rooms: unknown }[]) {
        if (list.type === "maintenance") continue;
        const rooms = Array.isArray(list.rooms) ? (list.rooms as Room[]) : [];
        for (const room of rooms) {
          for (const task of room.tasks ?? []) {
            const text = getTaskText(task)?.trim();
            if (!text) continue;
            const key = `${room.name}::${text}`;
            if (seen.has(key)) continue;
            seen.add(key);
            items.push({ key, room: room.name, text });
          }
        }
      }
    }
  } catch {
    // Anything unreadable never blocks the job: no arrival screen, no checklist gate.
    arrival = [];
    items = [];
  }
  return { arrival, items };
}

async function uploadArrivalPhoto(propertyId: string, taskId: string, file: File): Promise<string> {
  const prepared = await prepareImageFileForUpload(file);
  const path = `${propertyId}/${taskId}/simple-arrival/${crypto.randomUUID()}.jpg`;
  const { error } = await supabase.storage.from("cleaning-photos").upload(path, prepared, {
    contentType: "image/jpeg",
    upsert: false,
  });
  if (error) throw error;
  return supabase.storage.from("cleaning-photos").getPublicUrl(path).data.publicUrl;
}

export function SimpleCleanStep({
  propertyId,
  taskId,
  entryWalkthroughState,
  startedAt,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
  onComplete,
  onReportIssue,
  onBack,
  onSelectSegment,
  onUndoMaintenanceSkip,
  checklistIds = [],
}: Props) {
  const { t, tDynamic } = useTranslation();
  const [busy, setBusy] = useState(false);
  const flowRef = useRef<SimpleFlowState>(readSimpleFlowState(entryWalkthroughState));
  const [flow, setFlow] = useState<SimpleFlowState>(flowRef.current);
  const { data: stepData, isLoading: stepDataLoading } = useQuery({
    queryKey: ["clean-step-data", taskId, propertyId, checklistIds.join(",")],
    queryFn: () => loadCleanStepData(taskId, propertyId, checklistIds),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const arrivalTasks = stepData?.arrival ?? [];
  const cleanItems = stepData?.items ?? [];

  /** Save the job's answers the same way the other steps do: into entry_walkthrough_state. */
  const persist = async (patch: Partial<SimpleFlowState>) => {
    flowRef.current = { ...flowRef.current, simple_flow: true, ...patch };
    setFlow(flowRef.current);
    const { error } = await supabase
      .from("cleaning_tasks")
      .update({ entry_walkthrough_state: flowRef.current as never })
      .eq("id", taskId);
    if (error) {
      toast({ title: t("couldNotContinue"), description: error.message, variant: "destructive" });
    }
  };

  const answers = readArrivalAnswers(flow);
  const toggleArrival = (id: string) => {
    const cur = readArrivalAnswers(flowRef.current);
    void persist({ arrival_answers: { ...cur, [id]: { ...cur[id], done: !cur[id]?.done } } });
  };
  const addArrivalPhoto = async (id: string, file: File, replaceUrl?: string) => {
    try {
      const url = await uploadArrivalPhoto(propertyId, taskId, file);
      const cur = readArrivalAnswers(flowRef.current);
      const current = cur[id]?.photo_urls ?? [];
      const nextUrls = replaceUrl ? current.map((u) => (u === replaceUrl ? url : u)) : [...current, url];
      await persist({ arrival_answers: { ...cur, [id]: { ...cur[id], photo_urls: nextUrls } } });
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    }
  };
  const deleteArrivalPhoto = (id: string, url: string) => {
    const current = readArrivalAnswers(flowRef.current);
    void persist({
      arrival_answers: { ...current, [id]: { ...current[id], photo_urls: (current[id]?.photo_urls ?? []).filter((u) => u !== url) } },
    });
  };
  const ticks = new Set(flow.clean_checklist_ticks ?? []);
  const toggleTick = (key: string) => {
    const next = new Set(flowRef.current.clean_checklist_ticks ?? []);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    void persist({ clean_checklist_ticks: [...next] });
  };
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  const timerLabel = (() => {
    if (!startedAt) return "00:00";
    const ms = now - Date.parse(startedAt);
    if (!Number.isFinite(ms) || ms < 0) return "00:00";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  })();
  const compactTimer = timerLabel.replace(/^0(\d):/, "$1:");

  const startedLabel = (() => {
    if (!startedAt) return null;
    const d = new Date(startedAt);
    if (!Number.isFinite(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  })();

  const finish = async () => {
    setBusy(true);
    try {
      const prev = flowRef.current;
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({
          entry_walkthrough_state: {
            ...prev,
            simple_flow: true,
            clean_complete: true,
          },
        })
        .eq("id", taskId);
      if (error) throw error;
      onComplete();
    } catch (err) {
      toast({
        title: t("couldNotContinue"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const showArrival = !stepDataLoading && arrivalTasks.length > 0 && !flow.arrival_complete;
  const showChecklist = !stepDataLoading && !showArrival && cleanItems.length > 0;
  const tickedCount = cleanItems.filter((i) => ticks.has(i.key)).length;
  const itemsLeft = cleanItems.length - tickedCount;
  const timerRow = (
    <div className="flex shrink-0 items-center gap-[9px] border-b border-[#e6edf1] bg-white px-[18px] py-2.5">
      <div className="h-[9px] w-[9px] rounded-full bg-[#24a89a]" />
      <div className="flex-1 text-[14px] font-bold text-[#15776d]">{t("cleaningLabel")}</div>
      <div className="text-[20px] font-bold tabular-nums text-[#0f1720]">{compactTimer}</div>
    </div>
  );

  const skippedTitles = skippedMaintenanceTitles(readSimpleFlowState(entryWalkthroughState));
  const skipLabel = skippedTitles[0];

  return (
    <div className={CLEANER_SIMPLE_STEP_SHELL}>
      <SimpleStepHeader
        title={t("cleanTitle")}
        activeSegment={simpleFlowSegmentIndex("clean_free", { includeMaintenance, includeQuickCheck, includeGuestSetup })}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
      />

      {skipLabel && onUndoMaintenanceSkip ? (
        <div className="mx-5 mb-2 flex items-center gap-2 rounded-[12px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-3 py-2.5">
          <div className="min-w-0 flex-1 text-[13.5px] font-bold text-[#475569]">
            {t("maintenanceSkipped").replace("{{title}}", skipLabel)}
          </div>
          <button
            type="button"
            onClick={onUndoMaintenanceSkip}
            className="shrink-0 text-[14px] font-bold text-[#15776d]"
          >
            {t("undo")}
          </button>
        </div>
      ) : null}

      {stepDataLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#24a89a]" aria-hidden />
        </div>
      ) : showArrival ? (
        <>
          {timerRow}
          <ArrivalChecklistStep
            tasks={arrivalTasks}
            answers={answers}
            onToggle={toggleArrival}
            onAddPhoto={addArrivalPhoto}
            onDeletePhoto={deleteArrivalPhoto}
            onStart={() => void persist({ arrival_complete: true })}
          />
        </>
      ) : showChecklist ? (
        <>
          {timerRow}
          <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto bg-[#f4f7f9] px-3.5 pb-3 pt-4">
            <div className="flex flex-col gap-2.5 px-1 pb-1">
              <div className="flex items-baseline justify-between gap-2.5">
                <div className="text-[24px] font-bold tracking-[-0.5px] text-[#0f1720]">{t("cleanChecklistTitle")}</div>
                <div className="text-[17px] font-bold text-[#15776d]">
                  {t("arrivalDoneOf").replace("{{done}}", String(tickedCount)).replace("{{total}}", String(cleanItems.length))}
                </div>
              </div>
              <ProgressBars total={cleanItems.length} done={tickedCount} />
            </div>
            {cleanItems.map((item) => {
              const on = ticks.has(item.key);
              return (
                <div
                  key={item.key}
                  role="button"
                  onClick={() => toggleTick(item.key)}
                  className={cn(
                    "flex cursor-pointer items-center gap-3.5 rounded-2xl bg-white px-3.5 py-3",
                    on ? "border-[1.5px] border-[#24a89a]" : "border-[1.5px] border-[#e6edf1]",
                  )}
                >
                  <TickBox done={on} />
                  <div className="flex min-w-0 flex-1 flex-col gap-px">
                    <div className="text-[12.5px] font-bold text-[#8494a2]">{tDynamic(item.room)}</div>
                    <div className="text-[17.5px] font-bold text-[#0f1720]">{tDynamic(item.text)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={cn("flex shrink-0 flex-col gap-[9px] border-t border-[#e6edf1] bg-white", CLEANER_SIMPLE_CTA_FOOTER)}>
            <PhotoNeedNag propertyId={propertyId} taskId={taskId} />
            <div className={cn("text-center text-[15.5px] font-bold", itemsLeft === 0 ? "text-[#15776d]" : "text-[#475569]")}>
              {itemsLeft === 0
                ? t("cleanAllTicked")
                : itemsLeft === 1
                  ? t("cleanItemsLeftOne")
                  : t("cleanItemsLeftMany").replace("{{n}}", String(itemsLeft))}
            </div>
            <button
              type="button"
              disabled={busy || itemsLeft > 0}
              onClick={() => void finish()}
              className={cn(
                "flex h-16 items-center justify-center gap-2.5 rounded-[18px] text-[21px] font-bold",
                itemsLeft === 0
                  ? "bg-[#24a89a] text-white shadow-[0_8px_22px_rgba(36,168,154,0.32)] hover:bg-[#1f958a]"
                  : "bg-[#e6edf1] text-[#94a3b0]",
              )}
            >
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Check className="h-6 w-6" strokeWidth={3} />}
              {t("doneCleaning")}
            </button>
            {onReportIssue ? (
              <button
                type="button"
                onClick={onReportIssue}
                className="flex h-10 w-full items-center justify-center text-[15px] font-bold text-[#64748b] hover:text-[#0f1720]"
              >
                {t("reportIssue")}
              </button>
            ) : null}
          </div>
        </>
      ) : (
      <>
      <div className="flex flex-1 flex-col items-center justify-center gap-[18px] px-5">
        <div className="flex items-center gap-[9px]">
          <div className="h-[9px] w-[9px] rounded-full bg-[#24a89a]" />
          <div className="text-[15px] font-bold tracking-[0.4px] text-[#15776d]">{t("cleaningLabel")}</div>
        </div>
        <div className="text-[76px] font-bold tracking-[-2.5px] tabular-nums text-[#0f1720]">
          {timerLabel}
        </div>
        {startedLabel && (
          <div className="text-[16px] font-medium text-[#64748b]">
            {t("startedAt").replace("{{time}}", startedLabel)}
          </div>
        )}
      </div>

      <div className={`flex flex-col gap-3 ${CLEANER_SIMPLE_CTA_FOOTER}`}>
        <PhotoNeedNag propertyId={propertyId} taskId={taskId} />
        <button
          type="button"
          disabled={busy}
          onClick={() => void finish()}
          className="flex h-[68px] w-full items-center justify-center rounded-[18px] bg-[#24a89a] text-[23px] font-bold text-white hover:bg-[#1f958a]"
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("doneCleaning")}
        </button>
        {onReportIssue && (
          <button
            type="button"
            onClick={onReportIssue}
            className="flex h-12 w-full items-center justify-center rounded-[14px] text-[16px] font-bold text-[#64748b] hover:text-[#0f1720]"
          >
            {t("reportIssue")}
          </button>
        )}
      </div>
      </>
      )}
    </div>
  );
}
